import React, { useState, useMemo, useEffect } from 'react';
import { safeLocalStorage } from '@/lib/storage-utils';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBrand } from '@/context/BrandContext';
import { FolderOpen } from 'lucide-react';
import { DashboardHeader, BrandGridCard, BrandListRow, EmptyState } from '@/components/brand/dashboard';
import { DesignSystem } from '@/components/brand/DesignSystem';
import { DesignSystemEditor } from '@/components/brand/DesignSystemEditor';
import { DesignStudio } from '@/components/brand/DesignStudio';
import { SiteMapper } from '@/components/brand';
import { brandService } from '@/lib/brandService';
import { toast } from 'sonner';
import { MobileSubMenuHeader } from '@/components/layout';
import { navigationItems } from '@/lib/navigation';

interface BrandProps {
  isGlobalDarkMode?: boolean;
  setIsGlobalDarkMode?: (dark: boolean) => void;
  onMobileSubMenuToggle?: (menuId: string | null, subItems?: any[], menuLabel?: string) => void;
  isMobile?: boolean;
}

export default function Brand({ isGlobalDarkMode = true, setIsGlobalDarkMode, onMobileSubMenuToggle, isMobile = false }: BrandProps) {
  const { state, dispatch } = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const surface = isGlobalDarkMode ? 'bg-background' : 'bg-transparent';
  const text = isGlobalDarkMode ? 'text-white' : 'text-gray-900';
  const subtext = isGlobalDarkMode ? 'text-gray-300' : 'text-gray-600';

  // Determine current tab from URL
  const getCurrentTab = () => {
    if (location.pathname === '/brand') {
      return 'brand-dashboard';
    } else if (location.pathname.startsWith('/brand/design-system/') || location.pathname.startsWith('/brand/design-studio/design-system/') || location.pathname.startsWith('/brand/blueprints/design-system/')) {
      return 'design-system-editor';
    } else if (location.pathname === '/brand/design-system') {
      return 'design-system';
    } else if (location.pathname === '/brand/sitemaps') {
      return 'site-mapper';
    } else if (location.pathname === '/brand/blueprints' || location.pathname === '/brand/design-studio') {
      return 'blueprints';
    }
    return 'brand-dashboard';
  };

  const currentTab = getCurrentTab();

  // Brand Dashboard State
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'a-z' | 'z-a' | 'newest' | 'oldest'>('a-z');
  const [filterBy, setFilterBy] = useState<'all' | 'bookmarked' | 'live' | 'private' | 'draft'>('all');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showStatusPopup, setShowStatusPopup] = useState<string | null>(null);
  const [bookmarkedBrands, setBookmarkedBrands] = useState<Set<string>>(new Set());
  const [openStatusPopup, setOpenStatusPopup] = useState<string | null>(null);



  // Load bookmarked brands from localStorage on component mount
  useEffect(() => {
    const savedBookmarks = safeLocalStorage.getItem('bookmarkedBrands');
    if (savedBookmarks) {
      setBookmarkedBrands(new Set(JSON.parse(savedBookmarks)));
    }
  }, []);

  // Handle bookmark toggle
  const handleBookmarkToggle = (brandId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newBookmarks = new Set(bookmarkedBrands);

    if (newBookmarks.has(brandId)) {
      newBookmarks.delete(brandId);
    } else {
      newBookmarks.add(brandId);
    }

    setBookmarkedBrands(newBookmarks);
    safeLocalStorage.setItem('bookmarkedBrands', JSON.stringify(Array.from(newBookmarks)));
  };

  // Filter and sort brands based on search query, filter, and sort option
  const filteredAndSortedBrands = useMemo(() => {
    let brands = state.savedBrands;

    // Filter by search query
    if (searchQuery.trim()) {
      brands = brands.filter(brand =>
        brand.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by status/bookmark
    if (filterBy !== 'all') {
      brands = brands.filter(brand => {
        switch (filterBy) {
          case 'bookmarked':
            return bookmarkedBrands.has(brand.id);
          case 'live':
            return !brand.status || brand.status === 'live';
          case 'private':
            return brand.status === 'private';
          case 'draft':
            return brand.status === 'draft';
          default:
            return true;
        }
      });
    }

    // Sort brands
    switch (sortBy) {
      case 'a-z':
        brands = [...brands].sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'z-a':
        brands = [...brands].sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'newest':
        brands = [...brands].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case 'oldest':
        brands = [...brands].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        break;
      default:
        // Default to A-Z sorting
        brands = [...brands].sort((a, b) => a.name.localeCompare(b.name));
        break;
    }

    return brands;
  }, [state.savedBrands, searchQuery, sortBy, filterBy, bookmarkedBrands]);

  const handleDelete = async (id: string) => {
    // Confirm before deleting
    const brand = state.savedBrands.find(b => b.id === id);
    const brandName = brand?.name || 'this brand';

    if (!confirm(`Are you sure you want to delete "${brandName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      // Delete from database
      await brandService.deleteBrand(id);

      // Update local state
      const updatedBrands = state.savedBrands.filter(brand => brand.id !== id);
      safeLocalStorage.setItem('savedBrands', JSON.stringify(updatedBrands));
      dispatch({ type: 'LOAD_SAVED_BRANDS', payload: updatedBrands });
      setOpenMenuId(null);

      toast.success('Brand deleted successfully');
    } catch (error) {
      console.error('Error deleting brand:', error);
      toast.error('Failed to delete brand');
    }
  };

  const handlePrefetch = () => {
    // Prefetching is now handled by the context loading all brands in memory
  };

  const handleView = (brand: any) => {
    // Navigate immediately with brandId. BrandContext will pick up the data from cache instantly.
    navigate(`/preview?brandId=${brand.id}`);
  };

  const handleBrandClick = (brand: any) => {
    // Don't navigate if status popup is open
    if (openStatusPopup) {
      setOpenStatusPopup(null);
      return;
    }

    // Navigate immediately with brandId.
    navigate(`/preview?brandId=${brand.id}`);
  };


  const clearSearch = () => {
    setSearchQuery('');
  };

  const handleSortChange = (option: 'a-z' | 'z-a' | 'newest' | 'oldest') => {
    setSortBy(option);
    setShowSortMenu(false);
  };

  const handleFilterChange = (option: 'all' | 'bookmarked' | 'live' | 'private' | 'draft') => {
    setFilterBy(option);
    setShowFilterMenu(false);
  };

  // Handle brand status change
  const handleStatusChange = async (brandId: string, status: 'live' | 'private' | 'draft') => {
    try {
      await brandService.updateBrandStatus(brandId, status);

      const updatedBrands = state.savedBrands.map(brand =>
        brand.id === brandId ? { ...brand, status } : brand
      );
      dispatch({ type: 'LOAD_SAVED_BRANDS', payload: updatedBrands });
      setShowStatusPopup(null);
      setOpenStatusPopup(null);
      toast.success(`Brand status updated to ${status}`);
    } catch (error) {
      console.error('Error updating brand status:', error);
      toast.error('Failed to update brand status');
    }
  };

  // Handle team member assignment
  const handleAssignTeamMember = (brandId: string) => {
    // For now, just show an alert - this would open a team member selection modal
    alert(`Team member assignment feature for brand ${brandId} - would open team selection modal`);
    setOpenMenuId(null);
  };

  // Get status info
  const getStatusInfo = (status: string = 'live') => {
    switch (status) {
      case 'private':
        return { color: 'text-yellow-500', bgColor: 'bg-yellow-500', icon: 'Lock', label: 'Private' };
      case 'draft':
        return { color: 'text-gray-500', bgColor: 'bg-gray-500', icon: 'FileText', label: 'Draft' };
      case 'live':
      default:
        return { color: 'text-green-500', bgColor: 'bg-green-500', icon: 'Globe', label: 'Live' };
    }
  };


  // Close sort menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showSortMenu && !(event.target as Element).closest('.sort-menu-container')) {
        setShowSortMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSortMenu]);



  const renderTabContent = () => {
    switch (currentTab) {
      case 'brand-dashboard':
        return (
          <div className="w-full px-4 sm:px-6 md:px-10 py-4 sm:py-6">
            {/* Header Section with Search, Filter, Sort and View Toggle */}
            <DashboardHeader
              viewMode={viewMode}
              setViewMode={setViewMode}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              sortBy={sortBy}
              setSortBy={setSortBy}
              filterBy={filterBy}
              setFilterBy={setFilterBy}
              showSortMenu={showSortMenu}
              setShowSortMenu={setShowSortMenu}
              showFilterMenu={showFilterMenu}
              setShowFilterMenu={setShowFilterMenu}
              handleSortChange={handleSortChange}
              handleFilterChange={handleFilterChange}
              clearSearch={clearSearch}
              isMobile={isMobile}
            />

            {/* Brand List */}
            {filteredAndSortedBrands.length === 0 ? (
              <EmptyState
                isGlobalDarkMode={isGlobalDarkMode}
                searchQuery={searchQuery}
                clearSearch={clearSearch}
              />
            ) : viewMode === 'grid' ? (
              // Grid View
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredAndSortedBrands.map((brand) => (
                  <BrandGridCard
                    key={brand.id}
                    brand={brand}
                    isGlobalDarkMode={isGlobalDarkMode}
                    bookmarkedBrands={bookmarkedBrands}
                    handleBookmarkToggle={handleBookmarkToggle}
                    handleView={handleView}
                    handlePrefetch={handlePrefetch}
                    getStatusInfo={getStatusInfo}
                    openMenuId={openMenuId}
                    setOpenMenuId={setOpenMenuId}
                    handleDelete={handleDelete}
                    handleAssignTeamMember={handleAssignTeamMember}
                    showStatusPopup={showStatusPopup}
                    setShowStatusPopup={setShowStatusPopup}
                    handleStatusChange={handleStatusChange}
                  />
                ))}
              </div>
            ) : (
              // List View
              <div className="space-y-4">
                {filteredAndSortedBrands.map((brand) => (
                  <BrandListRow
                    key={brand.id}
                    brand={brand}
                    isGlobalDarkMode={isGlobalDarkMode}
                    bookmarkedBrands={bookmarkedBrands}
                    handleBookmarkToggle={handleBookmarkToggle}
                    handleBrandClick={handleBrandClick}
                    handlePrefetch={handlePrefetch}
                    getStatusInfo={getStatusInfo}
                    openMenuId={openMenuId}
                    setOpenMenuId={setOpenMenuId}
                    handleDelete={handleDelete}
                    handleAssignTeamMember={handleAssignTeamMember}
                    openStatusPopup={openStatusPopup}
                    setOpenStatusPopup={setOpenStatusPopup}
                    handleStatusChange={handleStatusChange}
                  />
                ))}
              </div>
            )}
          </div>
        );
      case 'design-system':
        return <DesignSystem isGlobalDarkMode={isGlobalDarkMode} />;
      case 'design-system-editor':
        return <DesignSystemEditor isGlobalDarkMode={isGlobalDarkMode} setIsGlobalDarkMode={setIsGlobalDarkMode} />;

      case 'site-mapper':
        return (
          <SiteMapper isMobile={isMobile} />
        );
      case 'blueprints':
        return (
          <DesignStudio isGlobalDarkMode={isGlobalDarkMode} isMobile={isMobile} />
        );
      default:
        return (
          <div className="text-center py-16">
            <div className={`w-24 h-24 rounded-md flex items-center justify-center mx-auto mb-6 ${isGlobalDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <FolderOpen className={`w-12 h-12 ${isGlobalDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            </div>
            <h3 className={`text-xl font-semibold mb-2 ${text}`}>No brands created yet</h3>
            <p className={`mb-6 ${subtext}`}>
              Create your first brand to get started with building your brand identity
            </p>
            <button
              onClick={() => navigate('/onboarding')}
              className="px-6 py-3 bg-blue-500 text-white rounded-md transition-colors hover:bg-blue-600"
            >
              Create Your First Brand
            </button>
          </div>
        );
    }
  };

  const handleMobileMenuToggle = () => {
    const brandMenuItem = navigationItems.find(item => item.id === 'brand');
    if (brandMenuItem && onMobileSubMenuToggle) {
      onMobileSubMenuToggle('brand', brandMenuItem.subItems, brandMenuItem.label);
    }
  };

  const handleMobileAdd = () => {
    switch (currentTab) {
      case 'brand-dashboard':
        navigate('/onboarding');
        break;
      case 'site-mapper':
        toast.info('Create new sitemap');
        break;
      default:
        break;
    }
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{currentTab === 'blueprints' ? 'Blueprints | AgencyPlus' : 'Brand | AgencyPlus'}</title>
        <link rel="canonical" href={currentTab === 'blueprints' ? '/brand/blueprints' : '/brand'} />
      </Helmet>

      {/* Mobile Sub-Menu Header - Hide on file-manager page and when browsing/editing color presets */}
      {isMobile &&
       currentTab !== 'site-mapper' &&
       currentTab !== 'design-system-editor' && (
        <MobileSubMenuHeader
          onMenuClick={handleMobileMenuToggle}
          onAddClick={handleMobileAdd}
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder={currentTab === 'brand-dashboard' ? 'Search brands...' : 'Search...'}
          showSearch={true}
          showAdd={true}
          isDarkMode={isGlobalDarkMode}
          actionButtons={[]}
        />
      )}

      <main className="w-full p-0">
        <div className={`min-h-screen ${surface} ${
          isMobile &&
          currentTab !== 'site-mapper' &&
          currentTab !== 'design-system-editor'
            ? 'pt-14'
            : ''
        }`}>
          {/* Content Area */}
          <div className="w-full">
            {renderTabContent()}
          </div>
        </div>
      </main>
    </div>
  );
}
